import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  dismissInstallPrompt,
  isAndroidDevice,
  isInstallPromptDismissed,
  isIosChrome,
  isIosInstallableBrowser,
  isIosSafari,
  isStandalonePwa
} from 'utils/pwaInstall'
import s from './styles.module.scss'

const IOS_CHROME_STEPS = [
  'Tap the ⋯ menu at the bottom-right of Chrome',
  'Tap Share…',
  'Scroll down and tap Add to Home Screen',
  'Tap Add in the top-right corner'
]

const IOS_SAFARI_STEPS = [
  'Tap the Share button at the bottom of Safari (square with an arrow)',
  'Scroll down and tap Add to Home Screen',
  'Tap Add in the top-right corner'
]

const IOS_OTHER_STEPS = [
  'Open this site in Safari (recommended) or use your browser Share menu',
  'Choose Add to Home Screen',
  'Confirm with Add'
]

function IosInstallSteps() {
  const steps = isIosChrome() ? IOS_CHROME_STEPS : isIosSafari() ? IOS_SAFARI_STEPS : IOS_OTHER_STEPS
  const browserLabel = isIosChrome() ? 'Chrome on iPhone' : isIosSafari() ? 'Safari' : 'your browser'

  return (
    <div className={s.iosHelp}>
      <p className={s.iosHelpLead}>
        iPhone does not allow websites to install with one tap. Use <strong>{browserLabel}</strong>:
      </p>
      <ol className={s.stepList}>
        {steps.map(step => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className={s.iosHelpNote}>After adding, open BCN from your home screen icon.</p>
    </div>
  )
}

export default function InstallAppPrompt() {
  const [visible, setVisible] = useState(false)
  const [mode, setMode] = useState(null)
  const [showIosHelp, setShowIosHelp] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (isStandalonePwa() || isInstallPromptDismissed()) return

    if (isIosInstallableBrowser()) {
      setMode('ios')
      setVisible(true)
      return
    }

    if (!isAndroidDevice()) return

    const onBeforeInstall = event => {
      event.preventDefault()
      setDeferredPrompt(event)
      setMode('android')
      setVisible(true)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall)
  }, [])

  const handleDismiss = useCallback(() => {
    dismissInstallPrompt()
    setShowIosHelp(false)
    setVisible(false)
  }, [])

  const handleAndroidInstall = useCallback(async () => {
    if (!deferredPrompt) return
    try {
      deferredPrompt.prompt()
      await deferredPrompt.userChoice
    } finally {
      setDeferredPrompt(null)
      setVisible(false)
    }
  }, [deferredPrompt])

  if (!mounted || !visible || !mode) return null

  const content = (
    <>
      {showIosHelp && mode === 'ios' && (
        <div
          className={s.backdrop}
          role="presentation"
          onClick={() => setShowIosHelp(false)}
        />
      )}

      <div className={`${s.banner} ${showIosHelp ? s.bannerExpanded : ''}`} role="dialog" aria-label="Install BCN app">
        <div className={s.content}>
          <div className={s.iconWrap}>
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" className={s.icon} />
          </div>

          <div className={s.copy}>
            <strong className={s.title}>Install BCN on your phone</strong>
            {mode === 'ios' && !showIosHelp && (
              <p className={s.steps}>Add BCN to your home screen for full-screen access.</p>
            )}
            {mode === 'android' && (
              <p className={s.steps}>Install for quick access, full-screen view, and notifications.</p>
            )}
            {mode === 'ios' && showIosHelp && <IosInstallSteps />}
          </div>

          <div className={s.actions}>
            {mode === 'ios' && (
              <button
                type="button"
                className={s.installBtn}
                onClick={() => setShowIosHelp(open => !open)}
              >
                {showIosHelp ? 'Hide steps' : 'How to install'}
              </button>
            )}
            {mode === 'android' && (
              <button type="button" className={s.installBtn} onClick={handleAndroidInstall}>
                Install
              </button>
            )}
            <button
              type="button"
              className={s.dismissBtn}
              onClick={handleDismiss}
              aria-label="Dismiss install prompt"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(content, document.body)
}
